/**
 * Local-first writes for comment/suggestion threads (sidecar blocks).
 *
 * A thread is ONE `block` row (`type = comment-thread`, `id = thread.id`) on
 * the commented page. Writes go to local SQLite and PowerSync uploads them via
 * the generic `block` write-path (the server re-stamps user/org authority
 * server-side, CC-40 as with any block). `syncPageBlocksMutation` ignores
 * sidecar rows, so the editor autosave can never wipe a thread.
 *
 * Before, this was ONE function (`upsertCommentThreadMutation`) that decided
 * INSERT-or-UPDATE with its own `SELECT`. That `SELECT` is the SAME window as
 * the phantom UPDATE (see `../powersync/verified-write.ts`): if it falls just
 * when the CRUD queue has emptied and the checkpoint does not yet bring the newly
 * created thread, it sees the row missing and retries an INSERT with the same id — it collides with
 * the PK (creating a thread and replying/resolving it quickly, the normal case of a
 * review). The caller (`usePageComments`) ALREADY KNOWS whether the thread is new (it
 * just created it with `createCommentThread`/`createSuggestionThread`) or
 * existing (it comes from `threads()`, derived from real rows in the block query),
 * so there is no need to guess: it is split into two mutations without
 * ambiguity.
 */
import { eq } from "drizzle-orm";
import { block, type PowerSyncDrizzleDB } from "@folio/powersync";
import { nowISO } from "../utils/date-helpers";
import { COMMENT_THREAD_BLOCK_TYPE } from "../documents/kinds";
import { patchRowVerified, type VerifiedWriteOptions } from "../powersync/verified-write";
import { type CommentThread, threadPlainText } from "./model";

/**
 * Sort key for sidecar rows: after any real content key ("z…" > base-62
 * content keys). Irrelevant for rendering (sidecars are filtered out of the
 * doc), but keeps `getPageBlocksQuery` ordering stable and content-first.
 */
const SIDECAR_SORT_KEY = "zz-sidecar";

/**
 * Creates the new thread (comment or suggestion). `thread.id` is ALWAYS a
 * fresh id (`generateId()` inside `createCommentThread`/
 * `createSuggestionThread`), so an INSERT here cannot collide with the
 * view-reconstruction window: there is no prior row with that id that could be
 * "temporarily absent".
 */
export async function createCommentThreadMutation(
  db: PowerSyncDrizzleDB,
  pageId: string,
  userId: string,
  thread: CommentThread,
): Promise<void> {
  const now = nowISO();
  await db.insert(block).values({
    id: thread.id,
    page_id: pageId,
    user_id: userId,
    type: COMMENT_THREAD_BLOCK_TYPE,
    content: JSON.stringify(thread),
    text: threadPlainText(thread),
    sort_key: SIDECAR_SORT_KEY,
    is_shared: 0,
    created_at: now,
    updated_at: now,
  });
}

/**
 * Updates an EXISTING thread (reply, resolve, reopen, review a
 * suggestion). The `thread` passed here always comes from `threads()`
 * (derived from real page rows), so the row exists unless it falls
 * in the PowerSync view window — therefore it goes through
 * `patchRowVerified` instead of a bare `UPDATE`.
 */
export async function updateCommentThreadMutation(
  db: PowerSyncDrizzleDB,
  thread: CommentThread,
  options?: VerifiedWriteOptions,
): Promise<void> {
  await patchRowVerified(
    db,
    block,
    block.id,
    thread.id,
    { content: JSON.stringify(thread), text: threadPlainText(thread) },
    options,
  );
}

/** Delete a thread's sidecar block (discard a suggestion / delete a thread). */
export async function deleteCommentThreadMutation(
  db: PowerSyncDrizzleDB,
  threadId: string,
): Promise<void> {
  await db.delete(block).where(eq(block.id, threadId));
}
